import React, { useRef, useState } from "react";
import Highstock from "highcharts/highstock";
import ReactHighcharts from "react-highcharts";
import { DS_CHART_TOKENS, withDsChartTheme } from './../../utils/chartTheme';
import Skeleton from "react-loading-skeleton";
import { RoundedSelect } from "../tailwind/atoms";
import OpdIcon from "./OpdIcon";
import { DownloadPngButton } from "./primitives";

const Charts = withDsChartTheme(ReactHighcharts.withHighcharts(Highstock));
const CLAIM_TREND_COLORS = {
  reimbursement: DS_CHART_TOKENS.semBlue400,
  cashless: DS_CHART_TOKENS.green400,
  total: DS_CHART_TOKENS.amber400,
};

const ClaimsTrends = ({
  data = {},
  loading = false,
  trendKeyMap = {
    "Week-on-Week": "week_on_week",
    "Month-on-Month": "month_on_month",
  },
  selectedTrend = "Week-on-Week",
}) => {
  const [metricType, setMetricType] = useState("amount"); // volume or amount
  const [selectedOption, setSelectedOption] = useState(selectedTrend);
  const cardRef = useRef(null);

  // Handle metric toggle
  const handleMetricToggle = (type) => setMetricType(type);

  // Handle dropdown change (RoundedSelect gives index)
  const handleTrendChange = (index) => {
    const value = Object.keys(trendKeyMap)[index];
    setSelectedOption(value);
  };

  const renderChart = () => {
    const trendData = data?.[trendKeyMap[selectedOption]] || {};

    if (!trendData || !trendData[metricType]) {
      return (
        <div className="w-full h-96 flex items-center justify-center text-gray-400">
          No data available
        </div>
      );
    }

    const categories = Object.keys(trendData[metricType]["total"] || {});

    if (!categories.length) {
      return (
        <div className="w-full h-96 flex items-center justify-center text-gray-400">
          No data available
        </div>
      );
    }

    // With only one or two periods of data, Highcharts stretches the bars
    // across the full plot width. Pad the axis with empty slots (centred) so a
    // sparse trend still renders with natural-width bars.
    const MIN_CATEGORY_SLOTS = 7;
    let plotCategories = categories;
    if (categories.length < MIN_CATEGORY_SLOTS) {
      const missing = MIN_CATEGORY_SLOTS - categories.length;
      const before = Math.floor(missing / 2);
      plotCategories = [
        ...Array(before).fill(""),
        ...categories,
        ...Array(missing - before).fill(""),
      ];
    }

    // Prepare series: Reimbursement, Cashless, Total
    const series = ["reimbursement", "cashless", "total"].map((key) => {
      const dataset = trendData[metricType][key] || {};
      return {
        name: key.charAt(0).toUpperCase() + key.slice(1),
        data: plotCategories.map((c) => (c === "" ? null : dataset[c] ?? null)),
        color: CLAIM_TREND_COLORS[key],
      };
    });

    const baseConfig = {
      chart: { type: "column" },
      title: { text: "", align: "left" },
      credits: { enabled: false },
      plotOptions: {
        column: { borderWidth: 0, borderRadius: 2, groupPadding: 0.15, pointPadding: 0.05, maxPointWidth: 56 },
      },
      xAxis: {
        categories: plotCategories,
        title: { text: selectedOption },
        labels: { style: { color: DS_CHART_TOKENS.gray600, fontFamily: "JetBrains Mono", fontSize: "10px" } },
      },
      yAxis: {
        title: { text: metricType === "volume" ? "Number of Claims" : "Amount (₹)" },
        allowDecimals: false,
        labels: { style: { color: DS_CHART_TOKENS.gray600, fontFamily: "JetBrains Mono", fontSize: "10px" } },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          let tooltip = `<div style="font-family:Inter,sans-serif;color:var(--ink);padding:2px 0"><span style="font-family:JetBrains Mono,monospace;color:var(--gray-600);font-size:11px">${this.x}</span><br/>`;
          this.points.forEach((p) => {
            const value =
              metricType === "amount"
                ? `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(p.y)}`
                : p.y;
            tooltip += `<span style="color:${p.color}">●</span> ${p.series.name}: <strong style="font-family:JetBrains Mono,monospace;color:var(--ink)">${value}</strong><br/>`;
          });
          return `${tooltip}</div>`;
        },
      },
      series,
    };

    return <Charts config={baseConfig} disableDownload />;
  };

  const trendData = data?.[trendKeyMap[selectedOption]] || {};
  const metricData = trendData?.[metricType] || {};
  const trendCategories = Object.keys(metricData.total || {});
  const latestPeriod = trendCategories[trendCategories.length - 1];
  const latestTotal = latestPeriod ? metricData.total?.[latestPeriod] : null;
  const latestValue = latestTotal == null
    ? "—"
    : metricType === "amount"
      ? `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(latestTotal)}`
      : new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(latestTotal);

  return (
    <div ref={cardRef} className="rounded-lg border border-gray-300 bg-white p-6 shadow-sm">
      {/* Header */}
      <div className="flex justify-between items-start mb-4 gap-6">
        <div className="flex items-start gap-4">
          <OpdIcon name="trends" className="h-6 w-6 rounded border border-blue-200 bg-blue-100 p-1.5 text-blue-700" />
          <div>
            <h1 className="mb-2 flex items-center gap-2 text-base font-fig-semibold leading-none text-gray-900">
              Overall Claim Trends
              <DownloadPngButton nodeRef={cardRef} fileName="overall-claim-trends" />
            </h1>
            <div className="font-jetbrains-mono text-3xl tabular-nums text-gray-900">{latestValue}</div>
            {latestPeriod && <div className="mt-1 text-xs font-fig-normal text-gray-600">Latest total · {latestPeriod}</div>}
          </div>
        </div>

        {/* Right side controls */}
        <div className="flex items-center gap-4">
          {/* Metric toggle */}
          <div className="flex space-x-1 overflow-x-auto rounded-md border border-gray-300 bg-gray-200 p-1">
            {["volume", "amount"].map((type) => {
              const isActive = metricType === type;
              return (
                <button
                  key={type}
                  onClick={() => handleMetricToggle(type)}
                  className={`rounded-sm px-3 py-1.5 text-sm font-fig-semibold transition-colors duration-200 focus:outline-none
                    ${isActive ? "rounded-md bg-white text-gray-900 shadow-sm" : "text-gray-600"}
                  `}
                >
                  {type === "volume" ? "Count" : "Value (₹)"}
                </button>
              );
            })}
          </div>
        </div>

        {/* Trend dropdown */}
        <RoundedSelect
          listClasses="w-56"
          selectOptions={Object.keys(trendKeyMap)}
          selectValues={[0, 1]}
          curvalue={selectedOption}
          selectHandler={handleTrendChange}
        />
      </div>

      {/* Chart */}
      {loading ? <Skeleton width="100%" height={400} /> : <div className="mt-4">{renderChart()}</div>}
    </div>
  );
};

export default ClaimsTrends;
